import React, { useState } from 'react';

const STYLES = [
  { label: 'Single Story', value: 'single' },
  { label: 'Double Story', value: 'double' },
  { label: 'Other', value: 'other' },
];

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xgvkqqye'; // Replace with your Formspree endpoint

export default function HouseWashEstimator() {
  const [step, setStep] = useState(1);
  const [customer, setCustomer] = useState({ name: '', phone: '', email: '' });
  const [size, setSize] = useState('');
  const [style, setStyle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const isNumber = val => /^\d+$/.test(val);
  const isEmail = val => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
  const getQuote = () => {
    const num = parseInt(size, 10);
    if (!isNumber(size) || !style || style === 'other') return null;
    if (num < 4000) return (num * 0.35).toFixed(2);
    return (num * 0.25).toFixed(2);
  };

  const needsPopup = style === 'other';

  const handleCustomerChange = e => setCustomer({ ...customer, [e.target.name]: e.target.value });

  const handleSubmit = async e => {
    e.preventDefault();
    if (!customer.name || !customer.phone || !isEmail(customer.email)) {
      setError('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: customer.name,
          phone: customer.phone,
          email: customer.email,
          house_size: size,
          house_style: style,
          estimate: getQuote(),
          _subject: 'New Exterior House Wash Estimate',
        }),
      });
      if (response.ok) {
        setSubmitted(true);
      } else {
        setError('Failed to submit. Please try again.');
      }
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <div className="w-1/3 h-2 bg-gray-200 rounded-full">
            <div className="h-full bg-[#b87c4c] rounded-full" style={{ width: `${(step / 3) * 100}%` }}></div>
          </div>
          <span className="text-sm text-gray-600">Step {step} of 3</span>
        </div>
      </div>
      {!submitted ? (
        <form onSubmit={handleSubmit} className="space-y-6">
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-[#4a3f35]">Step 1: Number of Stories</h3>
              <div className="space-y-2">
                {STYLES.map(opt => (
                  <label key={opt.value} className="flex items-start cursor-pointer">
                    <input type="radio" name="style" value={opt.value} checked={style === opt.value} onChange={() => setStyle(opt.value)} className="mt-1 mr-2" />
                    <div>
                      <span>{opt.label}</span>
                      {opt.value === 'other' && <span className="block text-xs text-gray-500 mt-1">(If you don't see your house style here, it may need our expertise to give you a quote.)</span>}
                    </div>
                  </label>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setStep(2)}
                className="w-full bg-[#8B4513] text-white py-2 px-4 rounded-md hover:bg-[#6B3410] transition-colors"
                disabled={!style}
              >
                Next
              </button>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-[#4a3f35]">Step 2: House Size (sq ft)</h3>
              <input
                type="text"
                value={size}
                onChange={e => setSize(e.target.value.replace(/[^\d]/g, ''))}
                className="w-full border rounded px-3 py-2 mb-4"
                placeholder="Enter square footage (numbers only)"
              />
              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  className="bg-[#8B4513] text-white py-2 px-4 rounded-md hover:bg-[#6B3410] transition-colors"
                  disabled={!size || !/^\d+$/.test(size)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-[#4a3f35]">Step 3: Your Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="name"
                    value={customer.name}
                    onChange={handleCustomerChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#b87c4c] focus:ring-[#b87c4c]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone <span className="text-red-500">*</span></label>
                  <input
                    type="tel"
                    name="phone"
                    value={customer.phone}
                    onChange={handleCustomerChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#b87c4c] focus:ring-[#b87c4c]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    name="email"
                    value={customer.email}
                    onChange={handleCustomerChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#b87c4c] focus:ring-[#b87c4c]"
                    required
                  />
                  {!isEmail(customer.email) && customer.email && (
                    <div className="text-xs text-red-500 mt-1">Please enter a valid email address.</div>
                  )}
                </div>
                {error && <div className="text-xs text-red-500 mt-1">{error}</div>}
              </div>
              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="bg-[#8B4513] text-white py-2 px-4 rounded-md hover:bg-[#6B3410] transition-colors"
                >
                  {submitting ? 'Submitting...' : 'Reveal Quote'}
                </button>
              </div>
              <div className="text-xs text-gray-500 mt-2">Your information will be sent to the owner, who will follow up with you about your estimate.</div>
            </div>
          )}
        </form>
      ) : (
        <div className="text-center">
          <h3 className="text-2xl font-bold text-[#4a3f35] mb-4">Your Quote</h3>
          {needsPopup ? (
            <div className="mb-4 p-4 bg-yellow-100 border-l-4 border-yellow-400 text-yellow-800 rounded">
              We will give you a call to discuss your specific needs.
            </div>
          ) : (
            <div className="mb-4 p-4 bg-green-100 border-l-4 border-green-400 text-green-800 rounded">
              <span className="block mt-2">Your approximate quote: <span className="font-bold text-lg">${getQuote()}</span></span>
              <div className="text-xs text-gray-500 mt-2">This is just an approximate quote. It's always good to call us to refine it!</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
} 